import { motion, AnimatePresence } from 'framer-motion';
import { FiLock, FiX } from 'react-icons/fi';
import { PREMIUM_FEATURES_LOST } from '../../config/premium-features';
import { usePromptDialog } from '../../hooks/usePromptDialog';
import { useScrimClose } from '../../hooks/useScrimClose';

interface TrialExpiredModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSubscribe: () => void;
}

const TrialExpiredModal = ({ isOpen, onDismiss, onSubscribe }: TrialExpiredModalProps) => {
  // A press begun in the panel and let go over the scrim keeps the dialog
  // (hooks/useScrimClose); at every width, since the scrim always shows.
  const scrim = useScrimClose({ onClose: onDismiss });

  // The dialog contract (hooks/usePromptDialog): focus in, Tab kept inside,
  // Escape and Android Back dismiss (as Escape did), focus back to the opener.
  const { dialogRef } = usePromptDialog(isOpen, onDismiss);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-(--ee-z-dialog) flex items-center justify-center p-4 bg-(--ee-scrim)/50 outline-none"
          onMouseDownCapture={scrim.onMouseDownCapture}
          onMouseUpCapture={scrim.onMouseUpCapture}
          onClick={scrim.onClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="trial-expired-title"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-(--ee-surface) rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-(--ee-tw-gray-200-line)">
              <h2
                id="trial-expired-title"
                className="text-lg font-semibold text-(--ee-tw-gray-900)"
              >
                Trial Ended
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={onDismiss}
                className="p-2 text-(--ee-tw-gray-500) hover:text-(--ee-tw-gray-700) hover:bg-(--ee-tw-gray-100-bg) rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Icon + message */}
              <div className="text-center">
                <div className="w-16 h-16 bg-(--ee-tw-gray-100-bg) rounded-full flex items-center justify-center mx-auto mb-3">
                  <FiLock className="text-2xl text-(--ee-tw-gray-500)" />
                </div>
                <h3 className="text-xl font-bold text-(--ee-tw-gray-900) mb-1">
                  Your Free Trial Has Ended
                </h3>
                <p className="text-sm text-(--ee-tw-gray-600)">
                  Subscribe to continue using premium features, or keep using the free plan.
                </p>
              </div>

              {/* Features they lost */}
              <div className="bg-(--ee-tw-gray-50-bg) border border-(--ee-tw-gray-200-line) rounded-xl p-4">
                <h4 className="text-sm font-medium text-(--ee-tw-gray-800) mb-2">
                  Premium features you no longer have access to:
                </h4>
                <ul className="space-y-1.5">
                  {PREMIUM_FEATURES_LOST.map((feature) => (
                    <li
                      key={feature}
                      className="text-sm text-(--ee-tw-gray-600) flex items-center gap-2"
                    >
                      <span className="w-1.5 h-1.5 bg-(--ee-tw-gray-400-bg) rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={onSubscribe}
                  className="w-full py-3 px-4 bg-primary text-(--ee-on-accent) rounded-xl font-medium hover:bg-(--ee-tw-orange-600-bg) transition-colors"
                >
                  Subscribe Now
                </button>
                <button
                  onClick={onDismiss}
                  className="w-full py-3 px-4 border border-(--ee-tw-gray-300-line) rounded-xl font-medium text-(--ee-tw-gray-700) hover:bg-(--ee-tw-gray-50-bg) transition-colors text-sm"
                >
                  Continue with Free Plan
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default TrialExpiredModal;
